import * as React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { STALE_DATA_TIMEOUT } from '@studio/constants/api-constants';
import { identifyPendoCustomer } from '@studio/helpers/identifyPendoCustomer';
import { useClient } from '@studio/hooks/use-client';
import { useSession } from '@studio/storeV2/authentication/session-store';
import { runCachedAbortableStoreFetch } from '@studio/storeV2/helpers/run-cached-abortable-store-fetch';
import type { PrefixCallback } from '@studio/types';
import {
  buildDeleteAfterOptions,
  buildEncoderChannelDeleteAfterOptions,
  customerAtom,
  customerLoadInFlightAtom,
  customerUsageInfoAtom,
  invalidateCustomerCacheAtom,
} from '../../customer';

type _FetchOptions = {
  force?: boolean;
  uuid?: string;
};

const useCustomer = ({ fetchOnMount = false }: { fetchOnMount?: boolean } = {}) => {
  const customerState = useAtomValue(customerAtom);
  const customerLoadInFlight = useAtomValue(customerLoadInFlightAtom);
  const customerUsageInfo = useAtomValue(customerUsageInfoAtom);
  const invalidateCustomerCache = useSetAtom(invalidateCustomerCacheAtom);
  const { customerId: sessionCustomerId } = useSession();

  const { callApi: getCustomer, isFetching: isClientFetching } = useClient({
    config: useClient.central.v3.customers.id.GET,
    params: { id: sessionCustomerId ?? '' },
  });

  const fetchCustomer = useAtomCallback(
    React.useCallback(
      async (get, set, options: _FetchOptions = {}) => {
        const { force, uuid } = options;
        const customerId = uuid ?? sessionCustomerId;
        if (!customerId) return;
        return runCachedAbortableStoreFetch(get, set, {
          dataAtom: customerAtom,
          emptyOnError: null,
          fetchWithSignal: (signal) => getCustomer(undefined, { signal, params: { id: customerId } }),
          forceRefresh: force,
          loadInFlightAtom: customerLoadInFlightAtom,
          onSuccess: (customer) => {
            if (customer) identifyPendoCustomer(customer);
          },
          staleTimeoutMs: STALE_DATA_TIMEOUT,
        });
      },
      [getCustomer, sessionCustomerId]
    )
  );

  const refreshCustomer = useAtomCallback(
    React.useCallback(
      async (get, set, options: { uuid?: string } = {}) => {
        const customerId = options.uuid ?? sessionCustomerId;
        if (!customerId) return;
        return runCachedAbortableStoreFetch(get, set, {
          dataAtom: customerAtom,
          emptyOnError: null,
          fetchWithSignal: (signal) => getCustomer(undefined, { signal, params: { id: customerId } }),
          forceSilentRefresh: true,
          loadInFlightAtom: customerLoadInFlightAtom,
          staleTimeoutMs: STALE_DATA_TIMEOUT,
        });
      },
      [getCustomer, sessionCustomerId]
    )
  );

  React.useEffect(() => {
    if (!fetchOnMount) return;
    void fetchCustomer();
  }, [fetchOnMount, fetchCustomer]);

  const isFetching = customerLoadInFlight != null || isClientFetching;

  const getDeleteAfterOptions = React.useCallback(
    (prefixT: PrefixCallback) => buildDeleteAfterOptions(customerState.data, prefixT),
    [customerState.data]
  );

  const getEncoderChannelDeleteAfterOptions = React.useCallback(
    (prefixT: PrefixCallback) => buildEncoderChannelDeleteAfterOptions(customerState.data, prefixT),
    [customerState.data]
  );

  return {
    customer: customerState.data,
    customerId: customerState.data?.uuid,
    customerUsageInfo,
    error: customerState.error,
    fetchCustomer,
    getDeleteAfterOptions,
    getEncoderChannelDeleteAfterOptions,
    hasFetched: customerState.hasFetched,
    hasLivePlan: Boolean(customerState.data?.webPlanId),
    hasRodOnly: Boolean(
      !customerState.data?.webPlanId && !customerState.data?.multisitePlanId && customerState.data?.vodPlanId
    ),
    invalidateCustomerCache,
    isFetching,
    masterEcg: customerState.data?.masterEcg,
    multisitePlanId: customerState.data?.multisitePlanId,
    refreshCustomer,
    regionId: customerState.data?.regionId,
    subscriptionDetails: customerState.data?.subscriptionDetails,
    timestamp: customerState.timestamp,
    vodPlanId: customerState.data?.vodPlanId,
    webPlan: customerState.data?.webPlan,
    webPlanId: customerState.data?.webPlanId,
    webPlanName: customerState.data?.webPlanName,
  };
};

export default useCustomer;
